import { Component, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import {
	PaymentTransaction,
	PaymentProcessingService,
} from '@lmc-app/core/services/payment-processing.service';
import {
	TransactionPreviewStatus,
	PREVIEW_STATUS_LABELS,
} from '@lmc-app/shared/enum/transaction-preview-status.enum';

export interface LoanAccountMatchDialogData {
	tx: PaymentTransaction;
}

/**
 * Corrects a transaction's banking details (institution / transit / account / recipient) to
 * resolve the matching loan account. The amount and due date are NOT edited here — matching a
 * specific scheduled payment is handled separately by the payments-due dialog.
 */
@Component({
	selector: 'app-loan-account-match-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatProgressSpinnerModule,
	],
	templateUrl: './loan-account-match-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./loan-account-match-dialog.component.scss'],
})
export class LoanAccountMatchDialogComponent implements OnDestroy {
	form: FormGroup;
	isValidating = false;
	/** Loan account resolved (loanNumber populated) — Apply is enabled. Carries the lookup result. */
	accountMatch: PaymentTransaction | null = null;
	/** Bank details did not resolve to any loan account (INVALID_BRANCH / CUSTOMER_NOT_FOUND). */
	accountNotMatched = false;
	validateError: string | null = null;

	private readonly destroy$ = new Subject<void>();

	constructor(
		public dialogRef: MatDialogRef<LoanAccountMatchDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: LoanAccountMatchDialogData,
		private readonly paymentProcessingService: PaymentProcessingService,
	) {
		const tx = data.tx;
		this.form = new FormGroup({
			institutionCode: new FormControl(tx.institutionCode, Validators.required),
			transitNumber: new FormControl(tx.transitNumber, Validators.required),
			accountNumber: new FormControl(tx.accountNumber, Validators.required),
			recipientName: new FormControl(tx.recipientName, Validators.required),
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get loanPaidOff(): boolean {
		return this.accountMatch?.status === TransactionPreviewStatus.PaidOff;
	}

	/**
	 * The loan resolved but the row still cannot be charged — today only incomplete customer
	 * data, which lives in NLS and cannot be corrected from here. `error` explains it.
	 */
	get unresolvedAfterMatch(): boolean {
		return !!this.accountMatch
			&& this.accountMatch.status !== TransactionPreviewStatus.Valid
			&& !this.loanPaidOff;
	}

	/** Only a clean lookup can be applied back to the preview row. */
	get canApply(): boolean {
		return this.accountMatch?.status === TransactionPreviewStatus.Valid;
	}

	/**
	 * Reason the matched loan cannot be charged. Unlike the preview table there is no status
	 * badge here, so the status label leads and `error` supplies the detail behind it
	 * (e.g. "Incomplete Info: Email, Phone Number").
	 */
	get unresolvedReason(): string {
		const status = this.accountMatch?.status;
		if (!status) return '';
		const label = PREVIEW_STATUS_LABELS[status];
		return this.accountMatch?.error ? `${label}: ${this.accountMatch.error}` : label;
	}

	onValidate(): void {
		if (this.form.invalid) return;

		this.isValidating = true;
		this.resetResults();

		// Keep the original amount + due date (already in cents / YYYY-MM-DD): this dialog only
		// re-resolves the loan account from the corrected bank details.
		const corrected: PaymentTransaction = {
			...this.data.tx,
			...this.form.value,
		};

		this.paymentProcessingService
			.relookupPaymentTransaction(corrected)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					this.isValidating = false;
					// A populated loanNumber means the bank details resolved to a loan account.
					// Whether the row is chargeable is a separate question the status answers.
					if (result && result.loanNumber) {
						this.accountMatch = result;
					} else {
						// Invalid branch, no matching customer, or no result — bank details unresolved.
						this.accountNotMatched = true;
					}
				},
				error: (err) => {
					this.isValidating = false;
					this.validateError = err?.error?.message ?? err?.message ?? 'Validation failed. Please try again.';
				},
			});
	}

	private resetResults(): void {
		this.accountMatch = null;
		this.accountNotMatched = false;
		this.validateError = null;
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onApply(): void {
		if (!this.canApply) return;
		this.dialogRef.close(this.accountMatch!);
	}
}
